/**
 * TV-Mode Presentation — Brief H
 *
 * Full-screen, high-legibility view of a published session result.
 * Designed to be cast to a display (TV, projector, conference room screen).
 *
 * Route: /prioritization/sessions/:sessionId/present
 *
 * - No nav, no sidebar, no chrome
 * - Dark background, large type
 * - Shareable URL — reads all data from DB on load
 * - Read-only — no data writes occur here
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Monitor, ChevronUp, ChevronDown, Pause, Play, Maximize2, ArrowLeft } from 'lucide-react';
import {
  fetchSessionPresentationData,
  type SessionPresentationResult,
} from '../../../stores/prioritization/sessionStore';
import { formatRank } from '@/lib/itemRankings';

// ─── Auto-scroll constants ─────────────────────────────────────────────────────
const SCROLL_SPEED_PX_PER_S = 30;
const AUTO_SCROLL_ITEM_THRESHOLD = 20;

export default function TVModePresentation() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [result, setResult] = useState<SessionPresentationResult | null>(null);
  const [loading, setLoading] = useState(true);

  // Auto-scroll state
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollPaused, setScrollPaused] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);

  // ── Fetch data — all reconstruction from DB on every load (Brief H §3) ────
  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    fetchSessionPresentationData(sessionId)
      .then((r) => {
        setResult(r);
        if (r.status === 'success' && r.data.rankedItems.length > AUTO_SCROLL_ITEM_THRESHOLD) {
          setIsScrolling(true);
        }
      })
      .catch(() => {
        setResult({ status: 'not_found' });
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  // ── Auto-scroll loop ────────────────────────────────────────────────────────
  const tick = useCallback((timestamp: number) => {
    if (!listRef.current) return;
    if (lastTimestampRef.current === null) {
      lastTimestampRef.current = timestamp;
    }
    const delta = (timestamp - lastTimestampRef.current) / 1000;
    lastTimestampRef.current = timestamp;

    const el = listRef.current;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (el.scrollTop >= maxScroll) {
      // Loop back to top
      el.scrollTop = 0;
    } else {
      el.scrollTop += SCROLL_SPEED_PX_PER_S * delta;
    }
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (isScrolling && !scrollPaused) {
      lastTimestampRef.current = null;
      animFrameRef.current = requestAnimationFrame(tick);
    } else {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    }
    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [isScrolling, scrollPaused, tick]);

  // ── Keyboard controls ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          navigate(-1);
          break;
        case ' ':
          e.preventDefault();
          if (isScrolling) setScrollPaused((p) => !p);
          break;
        case 'f':
        case 'F':
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
          } else {
            document.exitFullscreen().catch(() => {});
          }
          break;
        case 'ArrowDown':
          if (listRef.current) listRef.current.scrollTop += 120;
          break;
        case 'ArrowUp':
          if (listRef.current) listRef.current.scrollTop -= 120;
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate, isScrolling]);

  const handleToggleScrollPause = useCallback(() => {
    if (isScrolling) setScrollPaused((p) => !p);
  }, [isScrolling]);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // ── Shared TV-mode dark shell for non-data states ────────────────────────────
  function TVShell({ children }: { children: React.ReactNode }) {
    return (
      <div style={{ background: '#0f1117', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Monitor size={48} color="#475569" style={{ marginBottom: 24 }} />
        <div style={{ color: '#374151', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 32 }}>
          Priorities.ai
        </div>
        {children}
        <button
          onClick={() => navigate(-1)}
          style={{ marginTop: 32, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#1a1d26', color: '#64748b', border: '1px solid #252a3a', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem' }}
        >
          <ArrowLeft size={16} />
          Go back
        </button>
      </div>
    );
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ background: '#0f1117', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <Monitor size={48} color="#374151" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: '#475569', fontSize: '1rem' }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Case 2: not authorized (session not found via RLS, or truly missing) ────
  if (!result || result.status === 'not_found') {
    return (
      <TVShell>
        <h2 style={{ color: '#e2e8f0', fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
          You don't have access to this session result.
        </h2>
        <p style={{ color: '#64748b', fontSize: 'clamp(0.85rem, 1.5vw, 1rem)', lineHeight: 1.7, maxWidth: 440, textAlign: 'center' }}>
          If you believe this is an error, contact the session facilitator
          or cycle owner to request access.
        </p>
      </TVShell>
    );
  }

  // ── Case 3: result exists but not yet published ─────────────────────────────
  if (result.status === 'not_published') {
    return (
      <TVShell>
        <h2 style={{ color: '#e2e8f0', fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
          This result hasn't been published yet.
        </h2>
        <p style={{ color: '#64748b', fontSize: 'clamp(0.85rem, 1.5vw, 1rem)', lineHeight: 1.7, maxWidth: 440, textAlign: 'center' }}>
          Results are available for presentation once the session
          facilitator publishes them.
        </p>
      </TVShell>
    );
  }

  const { session, rankedItems, trackName, cycleName, sessionReport } = result.data;
  const publishedAt = sessionReport?.created_at
    ? new Date(sessionReport.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : session.created_at
    ? new Date(session.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // Determine if there's a cut line from the session report snapshot
  const cutLine: number | null = (() => {
    try {
      const snap = sessionReport?.inputs_snapshot;
      if (snap && typeof snap === 'object') {
        const cl = (snap as Record<string, unknown>).cutLine ?? (snap as Record<string, unknown>).cut_line;
        if (typeof cl === 'number') return cl;
      }
    } catch {
      // ignore
    }
    return null;
  })();

  const hasScores = rankedItems.some((item) => item.score != null);

  return (
    <div
      style={{
        background: '#0f1117',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* ── Header bar ──────────────────────────────────────────────────────── */}
      <header
        style={{
          background: '#1a1d26',
          borderBottom: '1px solid #252a3a',
          padding: '0 2rem',
          height: '8vh',
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        {/* Breadcrumb: Cycle › Track */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {cycleName && (
            <>
              <span
                style={{
                  color: '#64748b',
                  fontSize: 'clamp(0.65rem, 1.2vw, 0.85rem)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                }}
              >
                {cycleName}
              </span>
              {trackName && (
                <span style={{ color: '#374151', fontSize: '0.8rem' }}>›</span>
              )}
            </>
          )}
          {trackName && (
            <span
              style={{
                color: '#64748b',
                fontSize: 'clamp(0.65rem, 1.2vw, 0.85rem)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              {trackName}
            </span>
          )}
          {!cycleName && !trackName && (
            <span style={{ color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Session Result
            </span>
          )}
        </div>

        {/* Controls + Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isScrolling && (
            <button
              onClick={handleToggleScrollPause}
              title={scrollPaused ? 'Resume auto-scroll (Space)' : 'Pause auto-scroll (Space)'}
              style={{
                background: 'transparent',
                border: '1px solid #2a2f42',
                borderRadius: 6,
                padding: '4px 10px',
                color: '#64748b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.75rem',
              }}
            >
              {scrollPaused ? <Play size={13} /> : <Pause size={13} />}
              {scrollPaused ? 'Resume' : 'Pause'}
            </button>
          )}
          <button
            onClick={handleFullscreen}
            title="Toggle fullscreen (F)"
            style={{
              background: 'transparent',
              border: '1px solid #2a2f42',
              borderRadius: 6,
              padding: '4px 8px',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Maximize2 size={14} />
          </button>
          <button
            onClick={() => navigate(-1)}
            title="Exit (Esc)"
            style={{
              background: 'transparent',
              border: '1px solid #2a2f42',
              borderRadius: 6,
              padding: '4px 8px',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={14} />
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#475569',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            <Monitor size={14} color="#475569" />
            Priorities.ai
          </div>
        </div>
      </header>

      {/* ── Session title + subtitle ─────────────────────────────────────────── */}
      <div
        style={{
          padding: '2rem 2rem 1rem',
          flexShrink: 0,
        }}
      >
        <h1
          style={{
            color: '#e2e8f0',
            fontSize: 'clamp(1.4rem, 3vw, 2.2rem)',
            fontWeight: 700,
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          SESSION RESULT: {session.name.toUpperCase()}
        </h1>
        <p
          style={{
            color: '#64748b',
            fontSize: 'clamp(0.8rem, 1.4vw, 1rem)',
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          {publishedAt && <span>{publishedAt} · </span>}
          {rankedItems.length} item{rankedItems.length !== 1 ? 's' : ''} ranked · not of record
          {isScrolling && !scrollPaused && (
            <span style={{ marginLeft: 12, color: '#374151' }}>
              · Auto-scrolling (Space to pause)
            </span>
          )}
          {isScrolling && scrollPaused && (
            <span style={{ marginLeft: 12, color: '#374151' }}>
              · Paused (Space to resume · ↑↓ to scroll)
            </span>
          )}
        </p>
      </div>

      {/* ── Ranked items list ────────────────────────────────────────────────── */}
      <div
        ref={listRef}
        onClick={handleToggleScrollPause}
        style={{
          flex: 1,
          overflowY: rankedItems.length > AUTO_SCROLL_ITEM_THRESHOLD ? 'hidden' : 'auto',
          margin: '0 2rem 1rem',
          borderRadius: 10,
          border: '1px solid #252a3a',
          cursor: isScrolling ? 'pointer' : 'default',
        }}
      >
        {rankedItems.map((item, index) => {
          const isBelowCut = cutLine !== null && item.rank > cutLine;
          const isOdd = index % 2 === 0;
          const isCutBoundary = cutLine !== null && item.rank === cutLine + 1;

          return (
            <React.Fragment key={item.itemId}>
              {/* Cut line divider */}
              {isCutBoundary && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 1.5rem',
                    height: '2.5rem',
                    background: '#0f1117',
                    borderTop: '1px dashed #ef4444',
                    borderBottom: '1px dashed #ef4444',
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1, height: 0 }} />
                  <span
                    style={{
                      color: '#ef4444',
                      fontSize: 'clamp(0.65rem, 1.1vw, 0.8rem)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      padding: '2px 12px',
                      border: '1px dashed #ef4444',
                      borderRadius: 4,
                    }}
                  >
                    Cut line
                  </span>
                  <div style={{ flex: 1, height: 0 }} />
                </div>
              )}

              {/* Item row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 1.5rem',
                  minHeight: '9vh',
                  height: 'auto',
                  background: isOdd ? '#1e2130' : '#161924',
                  opacity: isBelowCut ? 0.45 : 1,
                  transition: 'background 0.15s',
                  gap: '1rem',
                }}
              >
                {/* Rank number */}
                <div
                  style={{
                    width: 'clamp(2.5rem, 4vw, 4rem)',
                    flexShrink: 0,
                    textAlign: 'right',
                    color: '#6b7280',
                    fontSize: 'clamp(1rem, 1.8vw, 1.5rem)',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  #{formatRank(item.rank, rankedItems.filter((other) => other.rank === item.rank).length > 1)}
                </div>

                {/* Separator */}
                <div
                  style={{
                    width: 1,
                    height: '2rem',
                    background: '#252a3a',
                    flexShrink: 0,
                  }}
                />

                {/* Item title */}
                <div
                  style={{
                    flex: 1,
                    color: '#f1f5f9',
                    fontSize: 'clamp(1.1rem, 2vw, 1.6rem)',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    padding: '1rem 0',
                  }}
                >
                  {item.title}
                </div>

                {/* Score */}
                {hasScores && (
                  <div
                    style={{
                      width: 'clamp(3.5rem, 6vw, 7rem)',
                      flexShrink: 0,
                      textAlign: 'right',
                      color: '#94a3b8',
                      fontSize: 'clamp(0.9rem, 1.6vw, 1.3rem)',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                    }}
                  >
                    {item.score != null
                      ? typeof item.score === 'number' && !Number.isInteger(item.score)
                        ? item.score.toFixed(2)
                        : String(item.score)
                      : '—'}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer
        style={{
          background: '#1a1d26',
          borderTop: '1px solid #252a3a',
          padding: '0 2rem',
          height: '5vh',
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#374151',
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
          }}
        >
          <Monitor size={12} />
          Priorities.ai
        </div>
        <div style={{ color: '#374151', fontSize: '0.7rem' }}>
          {publishedAt ? `Result Published ${publishedAt}` : 'Session Result'}
          {' · '}
          <span style={{ color: '#1e293b' }}>
            Esc to exit · Space to pause/resume · F for fullscreen
          </span>
        </div>
      </footer>

      {/* ── Scroll indicators (show only when scrolling and paused) ─────────── */}
      {isScrolling && scrollPaused && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <button
            onClick={() => listRef.current && (listRef.current.scrollTop -= 120)}
            style={{
              background: '#1a1d26',
              border: '1px solid #252a3a',
              borderRadius: 6,
              padding: 6,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            <ChevronUp size={18} />
          </button>
          <button
            onClick={() => listRef.current && (listRef.current.scrollTop += 120)}
            style={{
              background: '#1a1d26',
              border: '1px solid #252a3a',
              borderRadius: 6,
              padding: 6,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            <ChevronDown size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
